import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, router } from '@inertiajs/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { toast } from 'sonner'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form'
import { Toaster } from '../../components/ui/sonner'
import { Logo } from '../../components/logo'

const signupSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  password: z.string().min(8),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  path: ['password_confirmation'],
})

type SignupForm = z.infer<typeof signupSchema>

interface SignupProps {
  flash?: {
    success?: string
    error?: string
  }
  invitation_email?: string | null
}

export default function Signup({ flash, invitation_email }: SignupProps) {
  const { t } = useTranslation('auth')
  const form = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: invitation_email || '',
      first_name: '',
      last_name: '',
      password: '',
      password_confirmation: '',
    },
  })

  // Show flash messages as toasts
  useEffect(() => {
    if (flash?.success) {
      toast.success(flash.success)
    }
    if (flash?.error) {
      toast.error(flash.error)
    }
  }, [flash])

  const onSubmit = (data: SignupForm) => {
    router.post('/signup', { user: data })
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Toaster />

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo className="h-12" />
        </div>

        <Card className="w-full shadow-none">
          <CardHeader>
            <CardTitle>{t('signup.title')}</CardTitle>
            <CardDescription>{t('signup.subtitle')}</CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('signup.email')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t('signup.emailPlaceholder')}
                        autoComplete="email"
                        disabled={!!invitation_email}
                        {...field}
                      />
                    </FormControl>
                    {invitation_email && (
                      <p className="text-sm text-muted-foreground">
                        {t('signup.emailFromInvitation')}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('signup.firstName')}</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder={t('signup.firstNamePlaceholder')}
                          autoComplete="given-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('signup.lastName')}</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder={t('signup.lastNamePlaceholder')}
                          autoComplete="family-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('signup.password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t('signup.passwordPlaceholder')}
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password_confirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('signup.confirmPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t('signup.confirmPasswordPlaceholder')}
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? t('signup.submitting') : t('signup.submit')}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('signup.hasAccount')}{' '}
            <Link href="/login" className="text-primary hover:underline font-medium">
              {t('signup.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
